/**
 * Submission detail view — bigger photo, full metadata, tiny Leaflet
 * map. Same Approve / Reject buttons as the list.
 *
 * Implementation note on the data source: the admin API doesn't have a
 * "GET one submission" endpoint yet (it's a one-line addition on the
 * backend), so we fetch the pending list and find the matching id. For
 * v1 this is fine — the queue is rarely > 50 items — but it's worth
 * tracking. Falls back to "not found" if the id isn't in the current
 * pending list (e.g. someone else moderated it first).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import L from 'leaflet';

import {
  approveSubmission,
  listSubmissions,
  rejectSubmission,
  type AdminSubmission,
} from '../lib/api';
import RejectModal from '../components/RejectModal';

// Leaflet ships with relative-path image URLs that 404 under Vite. We
// inline a single emoji-ish marker via DivIcon instead — same look,
// no asset plumbing.
const markerIcon = L.divIcon({
  className: '',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#ea580c;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

export default function SubmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState<AdminSubmission | null | 'missing'>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const load = useCallback(async () => {
    if (id === undefined) return;
    setError(null);
    try {
      const resp = await listSubmissions({ status: 'pending', limit: 200 });
      const found = resp.submissions.find((s) => s.id === id);
      setSubmission(found ?? 'missing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submission.');
      setSubmission('missing');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleApprove = async () => {
    if (submission === null || submission === 'missing') return;
    setBusy(true);
    try {
      await approveSubmission(submission.id);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (reason: { en: string; th: string }) => {
    if (submission === null || submission === 'missing') return;
    setBusy(true);
    try {
      await rejectSubmission(submission.id, reason);
      setRejectOpen(false);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed.');
    } finally {
      setBusy(false);
    }
  };

  if (submission === null) {
    return <div className="py-16 text-center text-sm text-slate-500">Loading submission…</div>;
  }

  if (submission === 'missing') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm font-medium text-slate-700">
          This submission is no longer pending.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          It may have been approved or rejected by another moderator.
        </p>
        {error !== null && (
          <p className="mt-3 text-xs text-red-600">{error}</p>
        )}
        <Link
          to="/"
          className="mt-4 inline-block rounded-md bg-soi-500 px-4 py-2 text-sm font-medium text-white hover:bg-soi-600"
        >
          Back to queue
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <Link to="/" className="text-sm text-soi-600 hover:text-soi-700">
          ← Back to queue
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">{submission.name_th}</h1>
        {submission.name_en !== null && submission.name_en !== undefined && submission.name_en.length > 0 && (
          <p className="mt-1 text-base text-slate-600">{submission.name_en}</p>
        )}

        {submission.photo_url.length > 0 ? (
          <img
            src={submission.photo_url}
            alt={submission.name_en ?? submission.name_th}
            className="mt-4 max-h-[480px] w-full rounded-lg object-cover"
          />
        ) : (
          <div className="mt-4 flex h-64 w-full items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-400">
            No photo provided
          </div>
        )}

        <dl className="mt-6 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">Category</dt>
            <dd className="mt-0.5 text-slate-900">{submission.category ?? '—'}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Submitter</dt>
            <dd className="mt-0.5 truncate text-slate-900" title={submission.submitter_email}>
              {submission.submitter_email || '(anonymous)'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Submitted</dt>
            <dd className="mt-0.5 text-slate-900">
              {new Date(submission.submitted_at).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Address</dt>
            <dd className="mt-0.5 text-slate-900">{submission.address ?? '—'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="font-medium text-slate-500">Dishes</dt>
            <dd className="mt-0.5 text-slate-900">
              {submission.dishes.length > 0 ? submission.dishes.join(', ') : '—'}
            </dd>
          </div>
          {submission.suspicious_flags.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="font-medium text-slate-500">Suspicious flags</dt>
              <dd className="mt-1 flex flex-wrap gap-1.5">
                {submission.suspicious_flags.map((flag) => (
                  <span
                    key={flag}
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                  >
                    {flag}
                  </span>
                ))}
              </dd>
            </div>
          )}
        </dl>

        {error !== null && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => void handleApprove()}
            disabled={busy}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => setRejectOpen(true)}
            disabled={busy}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Reject
          </button>
        </div>
      </div>

      <aside className="lg:col-span-1">
        <h2 className="text-sm font-medium text-slate-500">Location</h2>
        <p className="mt-0.5 text-xs text-slate-400">
          {submission.location.lat.toFixed(5)}, {submission.location.lng.toFixed(5)}
        </p>
        <div className="mt-2 h-72 w-full overflow-hidden rounded-lg border border-slate-200">
          <MapContainer
            center={[submission.location.lat, submission.location.lng]}
            zoom={16}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker
              position={[submission.location.lat, submission.location.lng]}
              icon={markerIcon}
            />
          </MapContainer>
        </div>
        <a
          className="mt-2 inline-block text-xs text-soi-600 hover:text-soi-700"
          href={`https://www.google.com/maps/search/?api=1&query=${submission.location.lat},${submission.location.lng}`}
          target="_blank"
          rel="noreferrer"
        >
          Open in Google Maps ↗
        </a>
      </aside>

      <RejectModal
        open={rejectOpen}
        busy={busy}
        onCancel={() => setRejectOpen(false)}
        onConfirm={(reason) => void handleReject(reason)}
      />
    </div>
  );
}
